import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DownloadSimple, UploadSimple, FileArrowDown, FileArrowUp,
  Database, Table, FileCsv, FileXls, Archive,
  CheckCircle, Warning, Info, ArrowClockwise, Trash, Clock, FloppyDisk} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  exportBackupJson, exportBackupExcel,
  importBackupJson, importBackupExcel,
  previewImport, getBackupHistory
} from '../services/api';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const COLLECTION_LABELS = {
  contacts: 'Contacts', organizations: 'Organisations', opportunities: 'Opportunites',
  dossiers: 'Dossiers', documents: 'Documents', payments: 'Paiements',
  tasks: 'Taches', partners: 'Partenaires',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

export function BackupPage() {
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('merge');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await getBackupHistory();
      setHistory(res.data.items || []);
    } catch {} finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleExportJson = async () => {
    setExportingJson(true);
    try {
      const res = await exportBackupJson();
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_backup_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Backup JSON telecharge');
      fetchHistory();
    } catch { toast.error('Erreur export JSON'); }
    finally { setExportingJson(false); }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const res = await exportBackupExcel();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel telecharge');
      fetchHistory();
    } catch { toast.error('Erreur export Excel'); }
    finally { setExportingExcel(false); }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    const validExts = ['.json', '.zip', '.xlsx', '.xls'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExts.includes(ext)) {
      toast.error('Format non supporte. Utilisez JSON, ZIP ou Excel.');
      return;
    }
    setImportFile(file);
    setImportResult(null);
    setPreview(null);

    setPreviewing(true);
    try {
      const res = await previewImport(file);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur apercu');
    } finally { setPreviewing(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove('border-[#2D5A4C]', 'bg-[#2D5A4C]/5');
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add('border-[#2D5A4C]', 'bg-[#2D5A4C]/5');
  };

  const handleDragLeave = () => {
    dropRef.current?.classList.remove('border-[#2D5A4C]', 'bg-[#2D5A4C]/5');
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const isExcel = importFile.name.endsWith('.xlsx') || importFile.name.endsWith('.xls');
      const res = isExcel
        ? await importBackupExcel(importFile, importMode)
        : await importBackupJson(importFile, importMode);
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally { setImporting(false); }
  };

  const clearImport = () => {
    setImportFile(null);
    setPreview(null);
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="backup-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Sauvegardes & Import"
        subtitle="Export JSON/Excel et restauration du système"
        icon={<FloppyDisk size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#00AEEF', '#FF8E00']}
      />

      <div>
        <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Sauvegardes & Import
        </h1>
        <p className="text-[#5E6D66] mt-1">Exportez, sauvegardez et restaurez les donnees du CRM</p>
      </div>

      {/* Export Section */}
      <Card className="border-[#E8E6E1]" data-testid="export-section">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center">
              <FileArrowDown size={22} className="text-[#2D5A4C]" weight="duotone" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-[#1A2421]">Exporter les donnees</CardTitle>
              <p className="text-xs text-[#5E6D66] mt-0.5">Telecharger une sauvegarde complete de toutes les collections CRM</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <button
              onClick={handleExportJson}
              disabled={exportingJson}
              data-testid="export-json-btn"
              className="flex items-center gap-4 p-4 rounded-lg border-2 border-dashed border-[#E8E6E1] hover:border-[#2D5A4C] hover:bg-[#F9F8F5] transition-all text-left group"
            >
              <div className="w-12 h-12 rounded-lg bg-[#E8A365]/15 flex items-center justify-center">
                {exportingJson ? <Loader2 className="h-5 w-5 animate-spin text-[#E8A365]" /> : <Archive size={24} className="text-[#E8A365]" weight="duotone" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Export JSON (ZIP)</p>
                <p className="text-[11px] text-[#5E6D66] mt-0.5">Sauvegarde complete, format reutilisable pour l'import</p>
              </div>
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel}
              data-testid="export-excel-btn"
              className="flex items-center gap-4 p-4 rounded-lg border-2 border-dashed border-[#E8E6E1] hover:border-[#2D5A4C] hover:bg-[#F9F8F5] transition-all text-left group"
            >
              <div className="w-12 h-12 rounded-lg bg-[#62A8D4]/15 flex items-center justify-center">
                {exportingExcel ? <Loader2 className="h-5 w-5 animate-spin text-[#62A8D4]" /> : <Table size={24} className="text-[#62A8D4]" weight="duotone" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Export Excel (.xlsx)</p>
                <p className="text-[11px] text-[#5E6D66] mt-0.5">Multi-onglets, ideal pour l'analyse dans Excel/Google Sheets</p>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card className="border-[#E8E6E1]" data-testid="import-section">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D47A62]/10 flex items-center justify-center">
              <FileArrowUp size={22} className="text-[#D47A62]" weight="duotone" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-[#1A2421]">Importer des donnees</CardTitle>
              <p className="text-xs text-[#5E6D66] mt-0.5">Restaurer depuis une sauvegarde JSON ou Excel</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 pt-1">
            {/* Drop zone */}
            <div
              ref={dropRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-[#E8E6E1] rounded-lg p-8 text-center cursor-pointer hover:border-[#8DB0A1] transition-colors"
              data-testid="import-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".json,.zip,.xlsx,.xls"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                data-testid="import-file-input"
              />
              {importFile ? (
                <div className="flex items-center justify-center gap-3">
                  <Database size={24} className="text-[#2D5A4C]" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-[#1A2421]">{importFile.name}</p>
                    <p className="text-xs text-[#5E6D66]">{(importFile.size / 1024).toFixed(1)} Ko</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); clearImport(); }} className="text-[#D47A62]" data-testid="clear-import">
                    <Trash size={14} />
                  </Button>
                </div>
              ) : (
                <>
                  <UploadSimple size={32} className="mx-auto text-[#E8E6E1] mb-2" />
                  <p className="text-sm text-[#5E6D66]">Glissez un fichier ici ou <span className="text-[#2D5A4C] font-medium">parcourez</span></p>
                  <p className="text-[10px] text-[#8DB0A1] mt-1">Formats acceptes: JSON, ZIP, Excel (.xlsx)</p>
                </>
              )}
            </div>

            {/* Preview */}
            {previewing && (
              <div className="flex items-center gap-2 text-sm text-[#5E6D66]">
                <Loader2 className="h-4 w-4 animate-spin" /> Analyse du fichier...
              </div>
            )}
            {preview && (
              <div className="space-y-3" data-testid="import-preview">
                <div className="flex items-center gap-2">
                  <Info size={14} className="text-[#62A8D4]" />
                  <span className="text-sm font-medium text-[#1A2421]">Apercu: {preview.total} enregistrements trouves</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(preview.preview).map(([col, count]) => (
                    <div key={col} className="flex items-center justify-between p-2 rounded bg-[#F9F8F5] border border-[#E8E6E1]">
                      <span className="text-xs text-[#5E6D66]">{COLLECTION_LABELS[col] || col}</span>
                      <Badge variant="secondary" className="text-[10px]">{count}</Badge>
                    </div>
                  ))}
                </div>

                {/* Mode selector */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[#5E6D66]">Mode:</span>
                  <Select value={importMode} onValueChange={setImportMode}>
                    <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="merge">Fusion (ajouter les nouveaux)</SelectItem>
                      <SelectItem value="replace">Remplacement (tout ecraser)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {importMode === 'replace' && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="replace-warning">
                    <Warning size={16} className="text-red-500 mt-0.5 flex-shrink-0" weight="fill" />
                    <p className="text-xs text-red-700">
                      Le mode remplacement supprimera toutes les donnees existantes des collections importees avant insertion.
                      Cette action est irreversible.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-[#2D5A4C] hover:bg-[#1E4136]"
                  data-testid="confirm-import-btn"
                >
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UploadSimple size={16} className="mr-2" />}
                  {importMode === 'replace' ? 'Remplacer et importer' : 'Fusionner et importer'}
                </Button>
              </div>
            )}

            {/* Import result */}
            {importResult && (
              <div className="space-y-3 p-4 rounded-lg bg-[#F9F8F5] border border-[#E8E6E1]" data-testid="import-result">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-600" weight="fill" />
                  <span className="text-sm font-medium text-[#1A2421]">{importResult.message}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(importResult.results).map(([col, data]) => (
                    <div key={col} className="p-2 rounded bg-white border border-[#E8E6E1]">
                      <p className="text-xs font-medium text-[#1A2421]">{COLLECTION_LABELS[col] || col}</p>
                      <p className="text-[10px] text-[#5E6D66] mt-0.5">
                        {data.inserted} inseres{data.skipped > 0 ? `, ${data.skipped} ignores` : ''}
                      </p>
                    </div>
                  ))}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-600 font-medium">{importResult.errors.length} erreur(s):</p>
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <p key={i} className="text-[10px] text-red-500 mt-0.5">{err.collection}: {err.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="border-[#E8E6E1]" data-testid="backup-history">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8DB0A1]/15 flex items-center justify-center">
                <Clock size={22} className="text-[#8DB0A1]" weight="duotone" />
              </div>
              <CardTitle className="text-base font-semibold text-[#1A2421]">Historique</CardTitle>
            </div>
            <Button variant="outline" size="sm" onClick={fetchHistory} className="border-[#E8E6E1]" data-testid="refresh-history">
              <ArrowClockwise size={14} className="mr-1" /> Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#8DB0A1] mx-auto" /></div>
          ) : history.length === 0 ? (
            <p className="text-sm text-[#5E6D66] py-4 text-center">Aucune operation de sauvegarde</p>
          ) : (
            <div className="space-y-2">
              {history.map((h) => {
                const isExport = h.operation?.includes('export');
                return (
                  <div key={h.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#E8E6E1] hover:bg-[#FAFAF8] transition-colors" data-testid={`history-${h.id}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isExport ? 'bg-[#2D5A4C]/10' : 'bg-[#D47A62]/10'}`}>
                      {isExport ? <DownloadSimple size={14} className="text-[#2D5A4C]" weight="bold" /> : <UploadSimple size={14} className="text-[#D47A62]" weight="bold" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1A2421]">
                          {h.operation === 'export_json' ? 'Export JSON' :
                           h.operation === 'export_excel' ? 'Export Excel' :
                           h.operation === 'import_json' ? 'Import JSON' :
                           h.operation === 'import_excel' ? 'Import Excel' : h.operation}
                        </span>
                        {h.mode && <Badge variant="outline" className="text-[10px] border-[#E8E6E1]">{h.mode}</Badge>}
                        <Badge variant="secondary" className="text-[10px]">{h.total} enr.</Badge>
                      </div>
                      <p className="text-[10px] text-[#5E6D66] mt-0.5">par {h.user_name}</p>
                    </div>
                    <span className="text-[10px] text-[#8DB0A1] flex-shrink-0">{formatDate(h.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
